import React, { useEffect, useState } from 'react'
import { Alert, Avatar, Input, Modal } from '@rallyshift/design-system'
import api, { type Person } from '../../api/client'
import { useLocale } from '../../contexts/LocaleContext'
import { useOpenConversation } from '../../hooks/useOpenConversation'
import { personName } from './format'

interface Props {
  open: boolean
  onClose: () => void
}

/** Organisers can pick anyone; the server only offers volunteers the organisers. */
const NewConversationModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useLocale()
  const [query, setQuery] = useState('')
  const [people, setPeople] = useState<Person[]>([])
  const [loading, setLoading] = useState(false)
  const { open: openConversation, opening, error } = useOpenConversation()

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(() => {
      api.messaging
        .people(query.trim() || undefined)
        .then((list) => !cancelled && setPeople(list))
        .catch(() => !cancelled && setPeople([]))
        .finally(() => !cancelled && setLoading(false))
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query])

  const start = async (person: Person) => {
    await openConversation(() => api.messaging.openDirect(person.id))
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} title={t('messages.pickPerson')} width={480} closeLabel={t('common.close')}>
      <div className="space-y-3">
        <Input
          autoFocus
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          placeholder={t('messages.search')}
          aria-label={t('messages.search')}
        />
        {error && <Alert variant="danger">{error}</Alert>}
        <ul className="max-h-[50vh] divide-y divide-border overflow-y-auto">
          {people.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                disabled={opening}
                onClick={() => start(p)}
                className="flex w-full items-center gap-3 px-2 py-2.5 text-left transition-colors hover:bg-surface-sunken disabled:opacity-60"
              >
                <Avatar name={personName(p)} src={p.avatar_url} size={36} />
                <span className="flex-1 truncate text-sm font-semibold text-text-primary">{personName(p)}</span>
                {p.is_organiser && <span className="text-xs text-text-muted">{t('messages.organiser')}</span>}
              </button>
            </li>
          ))}
        </ul>
        {!loading && people.length === 0 && <p className="text-sm text-text-muted">{t('messages.noPeople')}</p>}
      </div>
    </Modal>
  )
}

export default NewConversationModal
